import { addDays } from "date-fns";

import {
  UISubscriptionStatus,
  UserSubscriptionAction,
  SubscriptionCancelReason,
  SubscriptionCancelReasonOption,
} from "common/types";
import { parseNumber } from "common/utils/numbers";
import { toDollars, toPercent } from "common/utils/formatting";
import { today } from "common/utils/dates";
import { Optional, TupleOfEntireUnion } from "common/utils/types";
import { Destination } from "common/router/types";

import type { StatusColor } from "client/common/components/content/Status";
import { ModalStatus } from "client/common/types";
import { urls } from "client/routes";

import { ClientSubscription, ListSubscription, SubscriptionForm } from "../types";

// Mirror of subscriptions.constants.EDB_MIN_LEAD_DAYS (BE): the minimum lead time in days
// before an expected delivery date is treated as too soon to fulfill.
export const edbMinLeadDays = 7;

// Earliest expected delivery date a client may select on a v2 (EDB) subscription.
export const minSelectableDeliveryDate = () => addDays(today(), edbMinLeadDays);

export const genSubscriptionDiscountContent = <T extends any>(
  firstDiscount: string,
  recurringDiscount: string,
  {
    none,
    onlyFirst,
    onlyRecurring,
    both,
  }: {
    none: () => T;
    onlyFirst: (first: string) => T;
    onlyRecurring: (recurring: string) => T;
    both: (first: string, recurring: string) => T;
  },
) => {
  const hasFirst = !!parseNumber(firstDiscount, 0);
  const hasRecurring = !!parseNumber(recurringDiscount, 0);

  if (!hasFirst && !hasRecurring) {
    return none();
  }
  if (!hasRecurring && hasFirst) {
    return onlyFirst(toPercent(firstDiscount));
  }
  if ((hasRecurring && !hasFirst) || recurringDiscount === firstDiscount) {
    return onlyRecurring(toPercent(recurringDiscount));
  }
  return both(toPercent(firstDiscount), toPercent(recurringDiscount));
};

export const genSubscriptionMaxDiscountContent = <T extends any>(
  firstMax: Optional<number>,
  recurringMax: Optional<number>,
  {
    none,
    onlyFirst,
    onlyRecurring,
    both,
  }: {
    none: () => T;
    onlyFirst: (first: string) => T;
    onlyRecurring: (recurring: string) => T;
    both: (first: string, recurring: string) => T;
  },
) => {
  const hasFirst = !!firstMax;
  const hasRecurring = !!recurringMax;

  if (!hasFirst && !hasRecurring) {
    return none();
  }
  if (!hasRecurring && hasFirst) {
    return onlyFirst(toDollars(firstMax, { roundCents: true }));
  }
  if ((hasRecurring && !hasFirst) || firstMax === recurringMax) {
    return onlyRecurring(toDollars(recurringMax, { roundCents: true }));
  }
  return both(
    toDollars(firstMax, { roundCents: true }),
    toDollars(recurringMax, { roundCents: true }),
  );
};

const exhaustiveStatusCheck = (status: never) => {
  console.warn(`Missing order status label for ${status}`);
  return "Unknown";
};

export const getLabelForSubscriptionStatus = (status: UISubscriptionStatus) => {
  switch (status) {
    case UISubscriptionStatus.Active:
      return "Active";
    case UISubscriptionStatus.Canceled:
      return "Canceled";
    case UISubscriptionStatus.Paused:
      return "Paused";
    case UISubscriptionStatus.FailedPayment:
      return "Failed Payment";
    case UISubscriptionStatus.Imported:
      return "Imported";
    default:
      return exhaustiveStatusCheck(status);
  }
};

const exhaustiveColorCheck = (status: never): StatusColor => {
  console.warn(`Missing order status color for ${status}`);
  return "greyscale";
};

export const getColorForSubscriptionStatus = (status: UISubscriptionStatus): StatusColor => {
  switch (status) {
    case UISubscriptionStatus.Active:
      return "success";
    case UISubscriptionStatus.Paused:
      return "warning";
    case UISubscriptionStatus.FailedPayment:
      return "danger";
    case UISubscriptionStatus.Canceled:
    case UISubscriptionStatus.Imported:
      return "greyscale";
    default:
      return exhaustiveColorCheck(status);
  }
};

const isPrimary = (action: UserSubscriptionAction) => action === UserSubscriptionAction.OrderNow;

type SortableSubscriptionAction =
  | UserSubscriptionAction.Skip
  | UserSubscriptionAction.Pause
  | UserSubscriptionAction.Resume
  | UserSubscriptionAction.Cancel;

// Increasing severity: skip (one-time) → pause (ongoing, reversible) → cancel (permanent)
const ACTION_DISPLAY_ORDER: TupleOfEntireUnion<SortableSubscriptionAction> = [
  UserSubscriptionAction.Skip,
  UserSubscriptionAction.Pause,
  UserSubscriptionAction.Resume,
  UserSubscriptionAction.Cancel,
];

const actionIndex = (action: UserSubscriptionAction) => {
  const idx = ACTION_DISPLAY_ORDER.findIndex((a) => a === action);
  return idx === -1 ? ACTION_DISPLAY_ORDER.length : idx;
};

type DynamicUserSubscriptionAction = {
  action: UserSubscriptionAction;
  label: string;
  dest: Destination;
  isPrimary: boolean;
};

export const getSubscriptionUserActions = (
  subscription: ClientSubscription | ListSubscription,
): DynamicUserSubscriptionAction[] => {
  const subscriptionActionToLink: Record<UserSubscriptionAction, Destination | undefined> = {
    [UserSubscriptionAction.Resume]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.Resume,
        visible: ModalStatus.Open,
      },
    }),
    [UserSubscriptionAction.Pause]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.Pause,
        visible: ModalStatus.Open,
      },
    }),
    [UserSubscriptionAction.Cancel]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.Cancel,
        visible: ModalStatus.Open,
      },
    }),
    [UserSubscriptionAction.Skip]: urls.subscriptions.detail({
      params: { id: subscription.id },
      search: {
        form: SubscriptionForm.SkipNextOrder,
        visible: ModalStatus.Open,
      },
    }),
    // Use add to cart button instead
    [UserSubscriptionAction.OrderNow]: undefined,
    // Not suported directly on the client side
    [UserSubscriptionAction.Modify]: undefined,
  };

  return (
    subscription.potential_user_actions
      // Set up the needed values
      .map(([action, label]) => ({
        action,
        label,
        dest: subscriptionActionToLink[action],
        isPrimary: isPrimary(action),
      }))
      // Remove any actions without a handler
      .flatMap((action) => (action.dest ? [action as typeof action & { dest: Destination }] : []))
      // Sort by display order; primary actions (OrderNow) sort last
      .sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) {
          return a.isPrimary ? 1 : -1;
        }
        return actionIndex(a.action) - actionIndex(b.action);
      })
  );
};

export const getSubscriptionCancelReasonOptions = (): SubscriptionCancelReasonOption[] => {
  const reasonToLabel: Partial<Record<SubscriptionCancelReason, string>> = {
    [SubscriptionCancelReason.ChangeInTreatment]: "My pet has switched to a different product",
    [SubscriptionCancelReason.Expensive]: "I found a lower price elsewhere",
    [SubscriptionCancelReason.ExcessProduct]: "I have enough supply on hand",
    [SubscriptionCancelReason.SubscriptionNotWanted]: "I prefer to buy this item as needed",
    [SubscriptionCancelReason.AdverseReaction]: "My pet had an adverse reaction to this item",
    [SubscriptionCancelReason.NoLongerClientOfClinic]: "I am no longer a client of this clinic",
    [SubscriptionCancelReason.PetPassedAway]: "My pet has passed away",
    [SubscriptionCancelReason.Other]: "Other",
  };
  return Object.entries(reasonToLabel).map(([value, label]) => ({
    value: value as SubscriptionCancelReason,
    label: label as string,
  }));
};
